import React from "react";
import { PhoneOff } from "lucide-react";

import { Button } from "../../../components/ui/button";
import WorkspaceCallControlCluster, {
  BOTTOM_CONTROL,
} from "./WorkspaceCallControlCluster";
import {
  CALL_BAR_CONTROLS_UNAVAILABLE,
  CALL_BAR_IDENTITY_DEFERRED,
  CALL_BAR_NO_CALL,
  CALL_BAR_RECORDING_UNAVAILABLE,
} from "../workspaceFrameCopy";

/**
 * The persistent bottom call-control region
 * (WORKSPACE-FEATURE-001 S5.1 layer 4, S15; authoritative original
 * `WorkspaceCallControls` at commit 23d500e27; reference
 * `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * AW-VIS-2 RESTORED THE ORIGINAL STRIP; PR-A'S CONTRACT IS UNCHANGED
 * ------------------------------------------------------------------
 * PR-A re-hosted Hang Up, the Hanging Up pending control and the
 * disposition panel here verbatim, and built a text-only bar around
 * them. The elements were right and the composition was not: the
 * authoritative strip is left call identity, a centred circular control
 * group, and a right status area, at roughly twice this bar's previous
 * height. That strip now comes from the shared, fixture-free
 * `WorkspaceCallControlCluster`, so the development shell and
 * Production render the same controls in the same order from the same
 * source.
 *
 * Nothing about the contract moved. This component still receives
 * pre-computed booleans it does not compute and callbacks it does not
 * define; the shell still owns every decision about when a control
 * appears and what it does. The props are deliberately booleans rather
 * than the shell's `presenceState` -- passing the raw state would mean
 * this component compared against a presence vocabulary it does not
 * own, which is how two state machines start to drift (the #685 defect
 * class). There is still exactly one state machine, and it is still the
 * shell's.
 *
 * WHAT IS WIRED, AND WHAT IS PRESENT BUT UNAVAILABLE
 * --------------------------------------------------
 * Hang Up is the only control with a Production contract, and it is
 * wired to the shell's existing handler, on the shell's existing
 * eligibility, with its existing testids: `workspace-hangup-button`
 * when actionable and `workspace-hangup-pending` while a hangup is in
 * flight. Outside those states it renders in its authoritative position
 * as unavailable, under the neutral `workspace-control-hang-up` id, so
 * no test or agent can mistake a scaffolded control for an actionable
 * one.
 *
 * Hold, Mute, Keypad, Voicemail, Transfer, Conference and Add Call keep
 * their designed positions and are visibly, programmatically and
 * semantically unavailable. This is deliberate product scaffolding, and
 * it is a narrower claim than it looks: S15.3 authorizes Hold, Dial Pad,
 * Hang Up and the disposition entry, and the remaining five are shown
 * as unavailable rather than offered -- the bar presents the original
 * design's shape while stating plainly that the capability is absent.
 * None of them binds a handler, issues a request, or fabricates
 * duration, recording or call identity. The cluster enforces that
 * itself; it is not left to this call site.
 *
 * Recording keeps its designed location and says it is unavailable.
 * There is no authoritative recording-state contract, and S15.2 forbids
 * stale or invented call context -- so the region exists and claims
 * nothing.
 *
 * CALLER IDENTITY
 * ---------------
 * S15.2 wants concise prospect identity in the bar. The Production
 * shell holds none -- deliberately, and AW-2C owns it. So the bar says
 * so in words rather than leaving a gap an agent could read as a name
 * that failed to render.
 */
export default function WorkspaceCallBar({
  conditionLabel = null,
  showHangup = false,
  showHangupPending = false,
  showDisposition = false,
  onHangup,
  dispositionOptions = {},
  dispositionInFlight = false,
  onDisposition,
  pauseAfterDisposition = false,
  onPauseAfterDispositionChange,
  dispositionError = null,
}) {
  const callIsCurrent = conditionLabel !== null;
  const reasonId = "workspace-call-bar-controls-reason";

  /*
    The only wiring entry in this file. Every other control is absent
    from the map and therefore unavailable by the cluster's own default
    -- forgetting to wire one can only ever produce a dead control,
    never a live one.
  */
  const controls = {
    [BOTTOM_CONTROL.HANG_UP]: {
      available: showHangup,
      pending: showHangupPending,
      onActivate: onHangup,
      testId: showHangupPending
        ? "workspace-hangup-pending"
        : showHangup
          ? "workspace-hangup-button"
          : "workspace-control-hang-up",
    },
  };

  return (
    <footer
      aria-label="Call controls"
      className="glass flex shrink-0 flex-col gap-3 rounded-xl px-4 py-4"
      data-testid="workspace-call-bar"
    >
      {/*
        The authoritative three-part strip: identity at the left edge,
        the control group centred, status at the right edge. `flex-1`
        with `basis-0` on the two outer groups is what keeps the control
        cluster optically centred regardless of how long the condition
        label or identity text is.
      */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 basis-0 items-center gap-3">
          <span
            className="flex items-center gap-2 text-sm text-slate-300"
            data-testid="workspace-call-bar-condition"
          >
            <PhoneOff
              className={callIsCurrent ? "h-4 w-4 text-emerald-400" : "h-4 w-4 text-slate-600"}
              aria-hidden="true"
            />
            {callIsCurrent ? conditionLabel : CALL_BAR_NO_CALL}
          </span>
          {callIsCurrent ? (
            <span className="truncate text-xs text-slate-500" data-testid="workspace-call-bar-identity">
              {CALL_BAR_IDENTITY_DEFERRED}
            </span>
          ) : null}
        </div>

        <WorkspaceCallControlCluster controls={controls} reasonId={reasonId} />

        <div
          className="flex min-w-0 flex-1 basis-0 items-center justify-end gap-1.5 text-xs text-slate-500"
          data-testid="workspace-recording-indicator"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-slate-600" aria-hidden="true" />
          {CALL_BAR_RECORDING_UNAVAILABLE}
        </div>
      </div>

      <p id={reasonId} className="sr-only">
        {CALL_BAR_CONTROLS_UNAVAILABLE}
      </p>

      {/*
        BB-12 -- disposition, and the only exit from Wrap-up.

        Rendered solely in Wrap-up, from choices the backend
        offered. Submission disables every button while one is in
        flight: `updateDISPO` is mutating and not proven
        idempotent, so a double-click must not become two
        dispositions on a real customer's record.
      */}
      {showDisposition ? (
        <div className="flex w-full flex-col gap-2" data-testid="workspace-disposition-panel">
          <p className="text-xs font-medium text-slate-300">
            Select a disposition to complete this call
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(dispositionOptions).map(([code, label]) => (
              <Button
                key={code}
                type="button"
                variant="outline"
                disabled={dispositionInFlight}
                onClick={() => onDisposition(code)}
                data-testid={`workspace-disposition-${code}`}
              >
                {label}
              </Button>
            ))}
          </div>
          {/*
            BB-12C. One-shot, client-held, default off. Disabled
            while a disposition is in flight so the agent cannot
            change the post-disposition behaviour of a request
            that has already been sent.
          */}
          <label
            className="flex items-center gap-2 text-xs text-slate-400"
            htmlFor="workspace-pause-after-disposition"
          >
            <input
              id="workspace-pause-after-disposition"
              type="checkbox"
              checked={pauseAfterDisposition}
              disabled={dispositionInFlight}
              onChange={(event) => onPauseAfterDispositionChange(event.target.checked)}
              data-testid="workspace-pause-after-disposition"
            />
            Pause after disposition
          </label>
          {dispositionError ? (
            <p
              className="text-xs leading-snug text-red-300"
              data-testid="workspace-disposition-error"
            >
              {dispositionError}
            </p>
          ) : null}
        </div>
      ) : null}
    </footer>
  );
}
